import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useLayoutEffect,
  useImperativeHandle,
  forwardRef,
} from "react"

const HANDLE_PX = 8
const HANDLE_HIT = HANDLE_PX + 4
const MIN_SIZE_PX = 10
const COORD_MAX = 10000

const FILL = "rgba(0, 123, 255, 0.2)"
const FILL_SEL = "rgba(0, 123, 255, 0.3)"
const STROKE = "rgba(0, 123, 255, 0.7)"
const STROKE_SEL = "#007bff"
const HANDLE_FILL = "#fff"
const HANDLE_STROKE = "#007bff"

const CURSOR_MAP = {
  nw: "nwse-resize", n: "ns-resize", ne: "nesw-resize",
  w: "ew-resize",                     e: "ew-resize",
  sw: "nesw-resize", s: "ns-resize", se: "nwse-resize",
}

function getHandles(r) {
  const mx = r.x + r.width / 2, my = r.y + r.height / 2
  const bx = r.x + r.width, by = r.y + r.height
  return {
    nw: [r.x, r.y], n: [mx, r.y], ne: [bx, r.y],
    w: [r.x, my],                  e: [bx, my],
    sw: [r.x, by],  s: [mx, by],  se: [bx, by],
  }
}

function findHandle(px, py, r) {
  for (const [id, [hx, hy]] of Object.entries(getHandles(r))) {
    if (Math.abs(px - hx) <= HANDLE_HIT && Math.abs(py - hy) <= HANDLE_HIT) return id
  }
  return null
}

function isInside(px, py, r) {
  return px >= r.x && px <= r.x + r.width && py >= r.y && py <= r.y + r.height
}

function normalizeRect(r) {
  let { x, y, width: w, height: h } = r
  if (w < 0) { x += w; w = -w }
  if (h < 0) { y += h; h = -h }
  return { x, y, width: w, height: h }
}

function clampRect(r, cw, ch) {
  let { x, y, width: w, height: h } = r
  if (x < 0) { w += x; x = 0 }
  if (y < 0) { h += y; y = 0 }
  return { x, y, width: Math.min(Math.max(0, w), cw - x), height: Math.min(Math.max(0, h), ch - y) }
}

function clampMoveRect(r, cw, ch) {
  return {
    ...r,
    x: Math.max(0, Math.min(r.x, cw - r.width)),
    y: Math.max(0, Math.min(r.y, ch - r.height)),
  }
}

function toPixelCoords(d, cw, ch) {
  return {
    x: (d.start_x / COORD_MAX) * cw,
    y: (d.start_y / COORD_MAX) * ch,
    width: (d.width / COORD_MAX) * cw,
    height: (d.height / COORD_MAX) * ch,
  }
}

function toDeviceCoords(r, cw, ch) {
  return {
    enable: true,
    start_x: Math.round((r.x / cw) * COORD_MAX),
    start_y: Math.round((r.y / ch) * COORD_MAX),
    width: Math.round((r.width / cw) * COORD_MAX),
    height: Math.round((r.height / ch) * COORD_MAX),
    qp: 0,
  }
}

function applyResize(orig, handle, dx, dy) {
  const r = { ...orig }
  if (handle.includes("n")) { r.y += dy; r.height -= dy }
  if (handle.includes("s")) r.height += dy
  if (handle.includes("w")) { r.x += dx; r.width -= dx }
  if (handle.includes("e")) r.width += dx
  return r
}

const DetectionZoneCanvas = forwardRef(function DetectionZoneCanvas(
  { base64Image, initialZones, onZonesChange, maxZones = 1 },
  ref,
) {
  const canvasRef = useRef(null)
  const imgRef = useRef(null)
  const [zones, setZones] = useState([])
  const [selectedIdx, setSelectedIdx] = useState(-1)
  const [size, setSize] = useState({ w: 0, h: 0 })

  const zonesRef = useRef(zones)
  const selRef = useRef(selectedIdx)
  const sizeRef = useRef(size)
  const dragRef = useRef(null)
  const docRef = useRef(null)

  useEffect(() => { zonesRef.current = zones }, [zones])
  useEffect(() => { selRef.current = selectedIdx }, [selectedIdx])
  useEffect(() => { sizeRef.current = size }, [size])

  const syncSize = useCallback(() => {
    const img = imgRef.current
    if (!img) return
    const nw = img.clientWidth, nh = img.clientHeight
    if (nw <= 0 || nh <= 0) return
    const { w: ow, h: oh } = sizeRef.current
    if (ow > 0 && oh > 0 && (ow !== nw || oh !== nh)) {
      const sx = nw / ow, sy = nh / oh
      setZones(prev =>
        prev.map(r => ({ x: r.x * sx, y: r.y * sy, width: r.width * sx, height: r.height * sy })),
      )
    }
    setSize({ w: nw, h: nh })
  }, [])

  const handleLoad = useCallback(() => syncSize(), [syncSize])

  useEffect(() => {
    window.addEventListener("resize", syncSize)
    return () => window.removeEventListener("resize", syncSize)
  }, [syncSize])

  useEffect(() => {
    if (!initialZones || !size.w || !size.h) return
    setZones(initialZones.filter(r => r.enable !== false).map(r => toPixelCoords(r, size.w, size.h)))
    setSelectedIdx(-1)
  }, [initialZones, size.w, size.h])

  useImperativeHandle(
    ref,
    () => ({
      getZonesNormalized() {
        const { w, h } = sizeRef.current
        return w && h ? zonesRef.current.map(r => toDeviceCoords(r, w, h)) : []
      },
      clearZones() {
        setZones([])
        setSelectedIdx(-1)
      },
    }),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    onZonesChange?.(zones)
  }, [zones]) // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const cvs = canvasRef.current
    if (!cvs || !size.w || !size.h) return
    cvs.width = size.w
    cvs.height = size.h
    const ctx = cvs.getContext("2d")
    ctx.clearRect(0, 0, size.w, size.h)

    zones.forEach((r, i) => {
      const sel = i === selectedIdx
      ctx.fillStyle = sel ? FILL_SEL : FILL
      ctx.fillRect(r.x, r.y, r.width, r.height)
      ctx.strokeStyle = sel ? STROKE_SEL : STROKE
      ctx.lineWidth = sel ? 2 : 1.5
      ctx.strokeRect(r.x, r.y, r.width, r.height)

      if (sel) {
        ctx.fillStyle = HANDLE_FILL
        ctx.strokeStyle = HANDLE_STROKE
        ctx.lineWidth = 1.5
        for (const [hx, hy] of Object.values(getHandles(r))) {
          ctx.fillRect(hx - HANDLE_PX / 2, hy - HANDLE_PX / 2, HANDLE_PX, HANDLE_PX)
          ctx.strokeRect(hx - HANDLE_PX / 2, hy - HANDLE_PX / 2, HANDLE_PX, HANDLE_PX)
        }
      }
    })
  }, [zones, selectedIdx, size])

  const canvasPos = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect()
    return rect ? { x: e.clientX - rect.left, y: e.clientY - rect.top } : { x: 0, y: 0 }
  }, [])

  const onMouseDown = useCallback(
    (e) => {
      if (e.button !== 0) return
      e.preventDefault()
      const p = canvasPos(e)
      const cur = zonesRef.current
      const sel = selRef.current
      const { w: cw, h: ch } = sizeRef.current
      if (!cw || !ch) return

      let drag = null

      if (sel >= 0 && sel < cur.length) {
        const h = findHandle(p.x, p.y, cur[sel])
        if (h) drag = { type: "resize", idx: sel, sx: p.x, sy: p.y, orig: { ...cur[sel] }, handle: h }
      }

      if (!drag) {
        for (let i = cur.length - 1; i >= 0; i--) {
          if (!isInside(p.x, p.y, cur[i])) continue
          setSelectedIdx(i)
          const h = findHandle(p.x, p.y, cur[i])
          drag = {
            type: h ? "resize" : "move",
            idx: i,
            sx: p.x,
            sy: p.y,
            orig: { ...cur[i] },
            handle: h,
          }
          break
        }
      }

      if (!drag) {
        if (cur.length >= maxZones) return
        setSelectedIdx(-1)
        drag = { type: "draw", sx: p.x, sy: p.y, idx: null }
      }

      dragRef.current = drag

      const onMove = (me) => {
        const d = dragRef.current
        if (!d) return
        me.preventDefault()
        const mp = canvasPos(me)
        const { w: mw, h: mh } = sizeRef.current
        if (!mw || !mh) return

        if (d.type === "draw") {
          const nr = clampRect(
            normalizeRect({ x: d.sx, y: d.sy, width: mp.x - d.sx, height: mp.y - d.sy }),
            mw,
            mh,
          )
          if (d.idx == null) {
            d.idx = zonesRef.current.length
            setZones(prev => [...prev, nr])
            setSelectedIdx(d.idx)
          } else {
            const ii = d.idx
            setZones(prev => prev.map((r, j) => (j === ii ? nr : r)))
          }
        } else if (d.type === "move") {
          const nr = clampMoveRect(
            { x: d.orig.x + mp.x - d.sx, y: d.orig.y + mp.y - d.sy, width: d.orig.width, height: d.orig.height },
            mw,
            mh,
          )
          const ii = d.idx
          setZones(prev => prev.map((r, j) => (j === ii ? nr : r)))
        } else if (d.type === "resize") {
          const nr = clampRect(
            normalizeRect(applyResize(d.orig, d.handle, mp.x - d.sx, mp.y - d.sy)),
            mw,
            mh,
          )
          const ii = d.idx
          setZones(prev => prev.map((r, j) => (j === ii ? nr : r)))
        }
      }

      const onUp = (ue) => {
        const d = dragRef.current
        if (d?.type === "draw" && d.idx != null) {
          const up = canvasPos(ue)
          const { w: uw, h: uh } = sizeRef.current
          const final_ = clampRect(
            normalizeRect({ x: d.sx, y: d.sy, width: up.x - d.sx, height: up.y - d.sy }),
            uw,
            uh,
          )
          if (final_.width < MIN_SIZE_PX || final_.height < MIN_SIZE_PX) {
            const ii = d.idx
            setZones(prev => prev.filter((_, j) => j !== ii))
            setSelectedIdx(-1)
          }
        }
        dragRef.current = null
        document.removeEventListener("mousemove", onMove)
        document.removeEventListener("mouseup", onUp)
        docRef.current = null
      }

      document.addEventListener("mousemove", onMove)
      document.addEventListener("mouseup", onUp)
      docRef.current = { move: onMove, up: onUp }
    },
    [canvasPos, maxZones],
  )

  useEffect(() => {
    return () => {
      if (docRef.current) {
        document.removeEventListener("mousemove", docRef.current.move)
        document.removeEventListener("mouseup", docRef.current.up)
      }
    }
  }, [])

  const onHover = useCallback(
    (e) => {
      if (dragRef.current) return
      const cvs = canvasRef.current
      if (!cvs) return
      const p = canvasPos(e)
      const cur = zonesRef.current
      const sel = selRef.current

      if (sel >= 0 && sel < cur.length) {
        const h = findHandle(p.x, p.y, cur[sel])
        if (h) { cvs.style.cursor = CURSOR_MAP[h]; return }
      }
      for (let i = cur.length - 1; i >= 0; i--) {
        if (isInside(p.x, p.y, cur[i])) { cvs.style.cursor = "move"; return }
      }
      cvs.style.cursor = "crosshair"
    },
    [canvasPos],
  )

  const onCtxMenu = useCallback(
    (e) => {
      e.preventDefault()
      const p = canvasPos(e)
      const cur = zonesRef.current
      for (let i = cur.length - 1; i >= 0; i--) {
        if (isInside(p.x, p.y, cur[i])) {
          setZones(prev => prev.filter((_, j) => j !== i))
          setSelectedIdx(prev => (prev === i ? -1 : prev > i ? prev - 1 : prev))
          return
        }
      }
    },
    [canvasPos],
  )

  useEffect(() => {
    const handler = (e) => {
      if ((e.key === "Delete" || e.key === "Backspace") && selRef.current >= 0) {
        const i = selRef.current
        setZones(prev => prev.filter((_, j) => j !== i))
        setSelectedIdx(-1)
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  if (!base64Image) return null

  const src = base64Image.startsWith("data:")
    ? base64Image
    : `data:image/jpeg;base64,${base64Image}`

  return (
    <div style={{ position: "relative", display: "inline-block", lineHeight: 0, maxWidth: "100%" }}>
      <img
        ref={imgRef}
        src={src}
        alt="Snapshot caméra"
        onLoad={handleLoad}
        style={{ maxWidth: "100%", display: "block" }}
        draggable={false}
      />
      {size.w > 0 && size.h > 0 && (
        <canvas
          ref={canvasRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: size.w,
            height: size.h,
          }}
          onMouseDown={onMouseDown}
          onMouseMove={onHover}
          onContextMenu={onCtxMenu}
        />
      )}
    </div>
  )
})

export default DetectionZoneCanvas
